"use client";

import React, { useEffect, useRef } from "react";
import { usePlaidLink } from "react-plaid-link";

import type { PlaidLinkWebSessionProps } from "./types";
import { useMemoizedFn } from "ahooks";

/**
 * Short-lived web Plaid Link session. Unmount destroys the react-plaid-link handler
 * so each openLink gets a fresh link.html initialize (not link-open.html resume).
 */
export const PlaidLinkWebSession: React.FC<PlaidLinkWebSessionProps> = ({
  linkToken,
  autoOpen = false,
  receivedRedirectUri,
  onLinkCompleted,
  onExit,
  onOpened,
}) => {
  const openedRef = useRef(false);

  const handleSdkSuccess = useMemoizedFn(() => {
    onLinkCompleted?.();
  });

  const config: Parameters<typeof usePlaidLink>[0] = {
    token: linkToken,
    onSuccess: handleSdkSuccess,
    onExit,
  };
  if (receivedRedirectUri) {
    config.receivedRedirectUri = receivedRedirectUri;
  }

  const { open, ready } = usePlaidLink(config);

  useEffect(() => {
    if (!autoOpen || !ready || openedRef.current) {
      return;
    }
    openedRef.current = true;
    open();
    onOpened?.();
  }, [autoOpen, onOpened, open, ready]);

  return null;
};
